import { create } from 'zustand';

interface AuthState {
  token: string | null;
  username: string | null;
  role: string; // always a string, 'viewer' if unauthenticated
  error: string | null;
  sessionExpired: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  expireSession: () => void;
  clearSessionExpired: () => void;
  isAuthenticated: () => boolean;
  getRole: () => string;
  initializeAuth: () => Promise<void>;
}

// localStorage so the session survives tab close and browser restart.
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USERNAME_KEY = 'auth_username';
const AUTH_ROLE_KEY = 'auth_role';

// Returns the JWT exp claim (seconds since epoch) or null if the token is
// malformed or has no exp. Used to avoid a backend round-trip on every page
// load — if the token is locally-expired we clear immediately; if it's still
// in window we trust it and let the next real API call surface any rejection
// (revoked user, rotated JWT_SECRET) via the 401 handler in api.ts.
function getTokenExp(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(payloadB64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  username: null,
  role: 'viewer',
  error: null,
  sessionExpired: false,

  login: async (username: string, password: string) => {
    try {
      // Use relative URL - let nginx handle the proxy
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        set({ error: 'Invalid credentials', role: 'viewer', token: null, username: null });
        return false;
      }

      const data = await response.json();

      localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
      localStorage.setItem(AUTH_USERNAME_KEY, data.username);
      localStorage.setItem(AUTH_ROLE_KEY, data.role);

      set({
        token: data.access_token,
        username: data.username,
        role: data.role,
        error: null
      });
      return true;
    } catch (error) {
      set({ error: 'Login failed', role: 'viewer', token: null, username: null });
      return false;
    }
  },

  logout: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USERNAME_KEY);
    localStorage.removeItem(AUTH_ROLE_KEY);
    set({ token: null, username: null, role: 'viewer', error: null, sessionExpired: false });
  },

  expireSession: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USERNAME_KEY);
    localStorage.removeItem(AUTH_ROLE_KEY);
    set({ token: null, username: null, role: 'viewer', error: null, sessionExpired: true });
  },

  clearSessionExpired: () => set({ sessionExpired: false }),

  isAuthenticated: () => !!get().token,

  getRole: () => get().role || 'viewer',

  initializeAuth: async () => {
    const savedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const savedUsername = localStorage.getItem(AUTH_USERNAME_KEY);
    const savedRole = localStorage.getItem(AUTH_ROLE_KEY);

    if (!savedToken || !savedUsername || !savedRole) {
      return;
    }

    const exp = getTokenExp(savedToken);
    if (exp !== null && exp * 1000 > Date.now()) {
      set({ token: savedToken, username: savedUsername, role: savedRole, error: null });
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USERNAME_KEY);
      localStorage.removeItem(AUTH_ROLE_KEY);
    }
  },
}));
